import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subscription, filter } from 'rxjs';
import { Site } from 'src/app/model/site';
import { AppState } from 'src/app/store/app.reducer';
import * as NotificationsActions from "src/app/store/notifications/notifications.actions";
import { selectCurrentSite } from 'src/app/store/site/site.selector';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ConfirmationService, TreeNode } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { GateState } from 'src/app/model/gate-state';
import { selectDeviceModels, selectDevicesStatus, selectGateStates } from 'src/app/store/device/device.selector';
import * as DeviceActions from 'src/app/store/device/device.actions';
import { DeviceStatus } from 'src/app/model/device-status';
import { EDeviceCategory } from 'src/app/conf/device-categories';
import { Device } from 'src/app/model/device';
import { DevicesTree } from 'src/app/model/device-tree';
import { ActivatedRoute, Router } from '@angular/router';
import { EDeviceModel, EDeviceThermoModel, EDeviceVctModel, isModelSubGateway } from 'src/app/conf/device-models';
import { convertDbStandartIcon, convertOldIcon, DeviceIcon } from 'src/app/conf/device-icons';
import { StandardDesignParameters, StandardMtmaLayout, StandardPageLayout, StandardTruncateString } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { AppComponent } from 'src/app/app.component';
import { Lang } from 'src/app/model/lang';
import { DEFAULT_LANG } from 'src/app/conf/languages';
import { selectCurrentLang, selectIsDraftMode, selectIsImpersMode, selectUser } from 'src/app/store/user/user.selector';
import { User } from 'src/app/model/user';
import * as AnalyticsActions from 'src/app/store/analytics/analytics.actions';
import { DeviceModel } from 'src/app/model/device-model';

@Component({
	selector: 'app-device-treetable',
	templateUrl: './device-treetable.component.html',
	providers: [ConfirmationService, DialogService],
})

export class DeviceTreeTableComponent implements OnInit, OnDestroy {
	gateState$: Observable<GateState[] | undefined>;
	gateStateSub: Subscription | undefined;
	site$: Observable<Site | undefined>;
	siteSub: Subscription | undefined;
	devicesStatus$: Observable<DeviceStatus[] | undefined>;
	devicesStatusSub: Subscription | undefined;
	deviceModels$: Observable<DeviceModel[] | undefined>;
	deviceModelsSub: Subscription | undefined;

	gateStates: GateState[];
	currentSite: Site;
	devicesStatus: DeviceStatus[] = [];
	gateways: Device[];

	devicesTree: DevicesTree;
	devices: TreeNode[] = [];
	deviceModels: DeviceModel[];

	currentUser: User;
	currentUser$: Observable<User | undefined>;
	currentUserSub: Subscription | undefined;

	currentLang$: Observable<Lang> = new Observable();
	currentLang: Lang = DEFAULT_LANG;

	isImpersMode: boolean;
	isImpersMode$: Observable<boolean | undefined>;
	isImpersModeSub: Subscription | undefined;

	isDraftMode: boolean;
	isDraftMode$: Observable<boolean | undefined>;
	isDraftModeSub: Subscription;

	ref: DynamicDialogRef | undefined;

	site: Site;
	acceptButtonStyle: string;
	rejectButtonStyle: string;

	screenWidth: number;
	MIN_WIDTH_COL6 = StandardDesignParameters.MIN_WIDTH_COL6;
	LG_WIDTH = StandardDesignParameters.LG_WIDTH;

	constructor(
		private store: Store<AppState>,
		public confirmationService: ConfirmationService,
		private translate: TranslateService,
		public dialogService: DialogService,
		private router: Router,
		private route: ActivatedRoute,
		public app: AppComponent
	) {
		this.initSelectors();
	}

	ngOnInit() {
		this.store.dispatch(NotificationsActions.setTopbarTitleId({
			stringId: 'DEVICES'
		}));
		this.onCurrentSiteChange();
		this.subscribeAll();
		this.onTreeDevicesChange();
		this.onResize();
		// catch if is default page
		setTimeout(() => {
			this.store.dispatch(AnalyticsActions.eventCustomPageView({ params: { page_title: 'List Device | AUTOMATIONS' } }));
		}, 2000);
	}

	initSelectors(): void {
		this.gateState$ = this.store.select(selectGateStates);
		this.site$ = this.store.select(selectCurrentSite);
		this.devicesStatus$ = this.store.select(selectDevicesStatus);
		this.currentLang$ = this.store.select(selectCurrentLang);
		this.isImpersMode$ = this.store.select(selectIsImpersMode);
		this.currentUser$ = this.store.select(selectUser);
		this.isDraftMode$ = this.store.select(selectIsDraftMode);
		this.deviceModels$ = this.store.select(selectDeviceModels);
	}

	subscribeAll(): void {
		this.gateStateSub = this.gateState$?.subscribe(gateStates => {
			this.gateStates = gateStates;
		});
		this.siteSub = this.site$?.pipe(
			filter(cs => !!cs)
		).subscribe((currentSite) => {
			this.site = currentSite;
		});
		this.currentLang$?.subscribe((lang: Lang) => {
			this.currentLang = lang;
		});
		this.isImpersModeSub = this.isImpersMode$?.subscribe(
			(isImpersMode) => {
				this.isImpersMode = isImpersMode;
			}
		);
		this.currentUserSub = this.currentUser$?.subscribe(
			(currentUser) => {
				this.currentUser = currentUser;
			}
		);
		this.isDraftModeSub = this.isDraftMode$.subscribe(
			(isDraftMode) => {
				this.isDraftMode = isDraftMode;
			}
		);
		this.deviceModelsSub = this.deviceModels$.subscribe(
			(deviceModels) => {
				this.deviceModels = deviceModels;
			}
		)
	}

	ngOnDestroy(): void {
		this.store.dispatch(NotificationsActions.resetTopbarTitleId());
		this.gateStateSub?.unsubscribe();
		this.siteSub?.unsubscribe();
		this.isImpersModeSub?.unsubscribe();
		this.currentUserSub?.unsubscribe();
		this.isDraftModeSub?.unsubscribe();
		this.deviceModelsSub?.unsubscribe();
	}

	onCurrentSiteChange(): void {
		this.siteSub = this.site$?.pipe(filter(currentSite => !!currentSite))
			.subscribe(currentSite => {
				this.currentSite = currentSite;
				if (currentSite.Devices !== undefined && currentSite.Devices.length > 0) {
					const deviceIds: number[] = currentSite.Devices.map(d => d.Id);
					this.store.dispatch(DeviceActions.getDevicesStatus({ deviceIds }));
				} else {
					this.devicesStatus = [];
					this.devices = [];
				}
			}
			);
	}

	onTreeDevicesChange(): void {
		this.devicesStatusSub = this.devicesStatus$?.pipe(filter(ds => ds.length > 0))
			.subscribe(
				devicesStatus => {
					if (!this.currentSite) {
						return;
					}
					this.devicesStatus = devicesStatus;
					this.devicesTree = this.filterTreeNode(this.currentSite.DevicesTree);
					//this.devicesTree = this.currentSite.DevicesTree;
					this.generateTreeNode();
					if (this.currentSite !== undefined && this.currentSite.Devices !== undefined) {
						this.gateways = this.currentSite.Devices.filter(d => (
							d.CategoryId === EDeviceCategory.Gateway ||
							d.CategoryId === EDeviceCategory.SubGateway
						));
					} else {
						this.gateways = [];
					}
				}
			);
	}

	filterTreeNode(devices: DevicesTree): DevicesTree {
		if (devices !== undefined && devices.Children !== undefined && devices.Children.length > 0) {
			const devicesNew = Object.assign({}, devices);
			devicesNew.Children = [];
			for (const children of devices.Children) {
				if (!this.isThermo(children) && !this.isVct(children)) {
					devicesNew.Children.push(children);
				}
			}
			return devicesNew;
		}
		return devices;
	}

	generateIcon(device: DevicesTree): string {
		let icon = '';
		if (device.ModelId !== EDeviceModel.MultiIO) {
			icon = 'cm-' + DeviceIcon[device.ModelId];
		} else {
			icon = 'cm-' + device.IconName.toLowerCase() || 'cm-' + DeviceIcon[device.ModelId] || 'cm-device-gateway';
		}
		switch (icon) {
			case 'cm-undefined': {
				const deviceModel = this.deviceModels.find((dm => dm.Id === device.ModelId));
				return convertDbStandartIcon(deviceModel.IconName);
			}
			default: {
				return convertOldIcon(icon);
			}
		}
		return icon;
	}

	generateTreeNode(): void {
		this.devices = [];
		if (this.devicesTree !== undefined) {
			for (const children of this.devicesTree.Children) {
				this.devices.push(this.createTreeNode(children));
			}
		}
	}

	createTreeNode(device: DevicesTree): TreeNode {
		let child = [];
		if (device.Children !== undefined && device.Children.length > 0) {
			for (const children of device.Children) {
				child.push(this.createTreeNode(children));
			}
		}
		if (child.length > 0) {
			const childListSubGateway = [];
			const childListNotGateway = [];
			for (const el of child) {
				if (isModelSubGateway(el.data.ModelId)) {
					childListSubGateway.push(el);
				} else {
					childListNotGateway.push(el);
				}
			}
			child = childListNotGateway.concat(childListSubGateway);
		}
		let tree = {};
		if (device.Id) {
			if (device.Expand === undefined) {
				device.Expand = true;
			}
			tree = { label: device.Name, data: device, icon: this.generateIcon(device), children: child, expanded: device.Expand };
		} else {
			if (device.Expand === undefined) {
				device.Expand = true;
			}
			tree = { label: 'NO_DEVICES', data: device, icon: "pi cm-cancel", children: child, expanded: device.Expand };
		}
		return tree;
	}

	isThermo(row: any): boolean {
		return Object.values(EDeviceThermoModel).includes(row.ModelId);
	}

	isVct(device: DevicesTree): boolean {
		if (device !== undefined && device.ModelId !== undefined) {
			return Object.values(EDeviceVctModel).includes(device.ModelId);
		} else if (device !== undefined && device.ProductId !== undefined) {
			return true;
		}
		return true;
	}

	getStatus(nodeId: number): string {
		if (this.devicesStatus.length > 0) {
			let device: DeviceStatus;
			this.devicesStatus.forEach(dev => {
				if (dev.Id === nodeId) {
					device = dev;
				}
			});
			if (device === undefined || !device.Online) {
				return this.translate.instant('OFFLINE');
			} else if (device.Errored) {
				return this.translate.instant('GENERIC_ERROR');
			} else {
				return this.translate.instant('ONLINE');
			}
			//return ((device) ? (device.Errored) ? this.translate.instant('GENERIC_ERROR') : ((device.Online) ? this.translate.instant('ONLINE') : this.translate.instant('OFFLINE')) : this.translate.instant('GENERIC_ERROR'));
		}
		return '';
	}

	getStatusColor(nodeId: any): string {
		if (this.devicesStatus.length > 0) {
			let device: DeviceStatus;
			this.devicesStatus.forEach(dev => {
				if (dev.Id === nodeId) {
					device = dev;
				}
			});
			if (device === undefined || !device.Online) {
				return 'danger';
			} else if (device.Errored) {
				return 'warning';
			} else {
				return 'success';
			}
			//return (device !== undefined && device.Errored) ? 'warning' : ((device !== undefined && device.Online) ? 'success' : 'danger');
		}
		return '';
	}

	selectDevice(deviceId: number): void {
		this.store.dispatch(DeviceActions.passwordVerifySupportReset());
		const urlRoute = '../devices/' + deviceId;
		this.router.navigate([urlRoute], { relativeTo: this.route });
	}

	onDeviceExpand(event: any) {
		if (event !== undefined && event.node !== undefined && event.node.data !== undefined && event.node.data.Expand !== undefined) {
			event.node.data.Expand = true;
		}
	}

	onDeviceCollapse(event: any) {
		if (event !== undefined && event.node !== undefined && event.node.data !== undefined && event.node.data.Expand !== undefined) {
			event.node.data.Expand = false;
		}
	}

	// Design
	@HostListener('window:resize')
	onResize(): void {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	layoutMarginResponsive(): string {
		return StandardPageLayout.layoutMarginResponsive(this.screenWidth);
	}

	actionLayout(position: string): string {
		return StandardMtmaLayout.actionLayout(this.screenWidth, position);
	}

	getSplitString(str: string): string {
		return StandardTruncateString.getSplitString(this.screenWidth, str, 15, 50, 100);
	}
}
